import React from 'react'
import { groupBy } from 'lodash-es'
import { InView } from 'react-intersection-observer'
import { FormattedMessage } from 'react-intl'

import { useWebinarFormatWebinarListInfiniteQuery } from '../../../../../hooks/useNewWebinarsQuery'
import LoaderComponent from '../../../../../components/LoaderComponent/LoaderComponent'

import { SessionCard } from './SessionCard/SessionCard'

import './SessionsDrawer.scss'

interface SessionsDrawerProps {
  seriesId: number
  seriesToken: string
  webinarId: number
}

// eslint-disable-next-line react/display-name, import-x/no-default-export
export default ({ seriesId, seriesToken, webinarId }: SessionsDrawerProps) => {
  const {
    data: associatedWebinars,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useWebinarFormatWebinarListInfiniteQuery({
    webinarFormatId: seriesId,
    token: seriesToken,
  })

  if (isLoading) {
    return (
      <div className="sessions-drawer">
        <div className="loading-wrapper">
          <LoaderComponent size="small" />
        </div>
      </div>
    )
  }

  if (!associatedWebinars) {
    return <FormattedMessage defaultMessage="No other webinars found" />
  }

  const allowedStateLabels = [
    'Live',
    'Waiting Room',
    'Upcoming',
    // 'On Demand', // handled below
    // 'Past', // handled below
  ]

  const webinarsByState = groupBy(
    associatedWebinars.pages
      .flatMap((page) => page.webinars)
      .filter((webinar) => webinar.id !== webinarId),
    'stateLabel'
  )

  const groupedWebinars = [
    ...allowedStateLabels.map((stateLabel) => ({
      stateLabel,
      webinars: webinarsByState[stateLabel] ?? [],
    })),
    {
      stateLabel: 'Previous',
      webinars: [
        ...(webinarsByState['On Demand'] ?? []),
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        ...(webinarsByState.Past ?? []),
      ],
    },
  ].filter(({ webinars }) => webinars.length > 0)

  const renderLoader = () => (
    <div className="loading-wrapper">
      <LoaderComponent size="small" />
    </div>
  )

  return (
    <div className="sessions-drawer">
      {groupedWebinars.map(
        ({ stateLabel, webinars }) =>
          webinars.length > 0 && (
            <div key={stateLabel} className="group">
              <div className="group-header">{stateLabel}</div>
              <div className="group-content">
                {webinars.map((webinar) => (
                  <SessionCard
                    key={webinar.id}
                    title={webinar.title}
                    thumbnailUrl={webinar.thumbnailMediumUrl}
                    linkUrl={webinar.link}
                    startDate={webinar.startDate}
                    endDate={
                      new Date(
                        webinar.startDate.getTime() +
                          (webinar.duration ?? 0) * 60000
                      )
                    }
                    names={webinar.speakers.map(
                      (speaker: { name: string }) => speaker.name
                    )}
                    isLive={webinar.state === 'live'}
                    state={webinar.stateLabel}
                  />
                ))}
              </div>
            </div>
          )
      )}
      {hasNextPage && renderLoader()}
      <InView
        className="infinite-loader-trigger"
        onChange={(inView) => {
          if (inView && !isFetchingNextPage) {
            void fetchNextPage()
          }
        }}
      />
    </div>
  )
}
